/**
 * Cuadros de mando.
 *
 * La regla que gobierna esta pantalla es del §107 y del catálogo de KPI:
 * **un indicador sin muestra suficiente vale `null`, no cero**. Un cero
 * dice «lo hicimos mal»; un `null` dice «no lo sabemos», y son cosas
 * distintas sobre las que se toman decisiones distintas. Por eso cada
 * indicador enseña su denominador: «94 % de 17 revisiones» y «94 % de 1.240»
 * no se leen igual.
 *
 * Sobre la jerarquía: arriba va **lo que obliga a hacer algo hoy** —el
 * cumplimiento vencido y las alertas—, y debajo los indicadores, que sirven
 * para saber cómo va la casa pero no para decidir qué se hace esta mañana.
 * Un panel donde todo pesa igual obliga a leerlo entero para encontrar lo
 * urgente, y por eso acaba sin leerse.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

interface KpiValue {
  code: string;
  label: string;
  unit: string;
  direction: string;
  /** `null` cuando no hay muestra suficiente. NO es un cero. */
  value: string | null;
  numerator: number;
  denominator: number;
  denominatorMeaning: string;
  reliable: boolean;
  note: string | null;
}

interface KpiTrend {
  code: string;
  current: string | null;
  previous: string | null;
  deltaPercent: string | null;
  improving: boolean | null;
}

interface Dashboard {
  period: { from: string; to: string };
  kpis: KpiValue[];
  trends: KpiTrend[];
}

interface Alert {
  kind: string;
  severity: string;
  message: string;
  entityType: string;
  entityId: string | null;
  dueDate: string | null;
  action: string;
  articleRef: string | null;
}

interface PlanningRow {
  site_name: string;
  due_count: string;
  overdue_count: string;
  unplannable_count: string;
  earliest_due_date: string | null;
}

/**
 * Unidades del catálogo del servidor (`KPI_UNITS`), en español.
 *
 * Estaban escritas en inglés —`PERCENT`, `DAYS`, `CURRENCY`— y no coincidían
 * con ninguna: el resultado era que ningún indicador imprimía su unidad y un
 * 94 % se leía «94». Una comparación de cadenas que no casa nunca no falla,
 * simplemente calla.
 */
function formatValue(kpi: KpiValue): string {
  if (kpi.value === null) return 'Sin datos';
  switch (kpi.unit) {
    case 'PORCENTAJE':
      return `${kpi.value} %`;
    case 'DIAS':
      return `${kpi.value} d`;
    case 'IMPORTE':
      return `${kpi.value} €`;
    default:
      return kpi.value;
  }
}

const PERIODS = [
  { id: 'mes', label: 'Mes en curso' },
  { id: '30d', label: '30 días' },
  { id: 'trimestre', label: 'Trimestre' },
  { id: 'anio', label: 'Año' },
] as const;

type PeriodId = (typeof PERIODS)[number]['id'];

function rangeFor(id: PeriodId): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();

  switch (id) {
    case 'mes':
      return { from: new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10), to };
    case 'trimestre':
      return { from: new Date(Date.UTC(y, m - 2, 1)).toISOString().slice(0, 10), to };
    case 'anio':
      return { from: new Date(Date.UTC(y, 0, 1)).toISOString().slice(0, 10), to };
    default:
      return {
        from: new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10),
        to,
      };
  }
}

const SEVERITY_TONE: Record<string, string> = {
  URGENTE: 'bad',
  ALTA: 'bad',
  MEDIA: 'warn',
  BAJA: 'info',
};

/** Miles con punto, que es como se leen aquí. */
function miles(value: string | number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function ReportsScreen(): React.ReactNode {
  const { api } = useSession();

  const [periodId, setPeriodId] = useState<PeriodId>('30d');
  const [data, setData] = useState<Dashboard | null>(null);
  const [alerts, setAlerts] = useState<{ shown: Alert[]; grouped: Alert[] } | null>(null);
  const [planning, setPlanning] = useState<PlanningRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const period = rangeFor(periodId);

  const load = useCallback(async (from: string, to: string) => {
    setLoading(true);
    try {
      const dashboard = await api.get<Dashboard>(`/analytics/dashboard?from=${from}&to=${to}`);
      setData(dashboard);
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'No se han podido cargar los indicadores.',
      );
    } finally {
      setLoading(false);
    }

    // Los dos paneles de arriba son opcionales: quien no tenga permiso de
    // mantenimiento sigue viendo sus indicadores en lugar de una pantalla
    // rota.
    void api
      .get<{ shown: Alert[]; grouped: Alert[] }>('/analytics/alerts')
      .then(setAlerts)
      .catch(() => setAlerts(null));
    void api
      .get<{ items: PlanningRow[] }>('/planning/queue?pageSize=200')
      .then((r) => setPlanning(r.items))
      .catch(() => setPlanning(null));
  }, [api]);

  useEffect(() => {
    void load(period.from, period.to);
  }, [load, period.from, period.to]);

  const trendOf = (code: string): KpiTrend | undefined => data?.trends.find((t) => t.code === code);

  const overdue = (planning ?? []).reduce((n, r) => n + Number(r.overdue_count), 0);
  const due = (planning ?? []).reduce((n, r) => n + Number(r.due_count), 0);
  const unplannable = (planning ?? []).reduce((n, r) => n + Number(r.unplannable_count ?? 0), 0);
  const sitesOverdue = (planning ?? []).filter((r) => Number(r.overdue_count) > 0).length;
  const worst = (planning ?? [])
    .filter((r) => Number(r.overdue_count) > 0)
    .sort((a, b) => Number(b.overdue_count) - Number(a.overdue_count))
    .slice(0, 5);

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Cuadros de mando</h1>
        <div className="chips" role="group" aria-label="Periodo">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`chip${p.id === periodId ? ' chip--on' : ''}`}
              aria-pressed={p.id === periodId}
              onClick={() => setPeriodId(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className="screen__count">
          {period.from} → {period.to}
        </span>
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {/* ── Lo que obliga a hacer algo hoy ───────────────────────────── */}
      {planning !== null && (
        <div className="panel panel--first">
          <h2 className="panel__title">Cumplimiento hoy</h2>
          <div className="figures">
            <div className={`figure${overdue > 0 ? ' figure--bad' : ''}`}>
              <span className="figure__value">{miles(overdue)}</span>
              <span className="figure__label">obligaciones vencidas</span>
              <span className="figure__meta">
                en {sitesOverdue} {sitesOverdue === 1 ? 'centro' : 'centros'}
              </span>
            </div>
            <div className="figure">
              <span className="figure__value">{miles(due)}</span>
              <span className="figure__label">equipos con plan</span>
              <span className="figure__meta">bajo contrato y con vencimiento calculado</span>
            </div>
            <div className={`figure${unplannable > 0 ? ' figure--warn' : ''}`}>
              <span className="figure__value">{miles(unplannable)}</span>
              <span className="figure__label">sin planificar</span>
              <span className="figure__meta">
                {unplannable > 0
                  ? 'no generan obligación: nadie los revisa'
                  : 'todo el parque entra en el plan'}
              </span>
            </div>
          </div>

          {worst.length > 0 && (
            <table className="table__grid">
              <thead>
                <tr>
                  <th>Centro con más vencido</th>
                  <th className="table__num">Vencidas</th>
                  <th>Desde</th>
                </tr>
              </thead>
              <tbody>
                {worst.map((r) => (
                  <tr key={r.site_name}>
                    <td>{r.site_name}</td>
                    <td className="table__num">{r.overdue_count}</td>
                    <td>{r.earliest_due_date ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Alertas ──────────────────────────────────────────────────── */}
      {alerts !== null && (alerts.shown.length > 0 || alerts.grouped.length > 0) && (
        <div className="panel">
          <h2 className="panel__title">Alertas</h2>
          <ul className="alerts">
            {[...alerts.shown, ...alerts.grouped].map((a, index) => (
              <li key={`${a.kind}-${a.entityId ?? index}`} className="alerts__item">
                <span className={`pill pill--${SEVERITY_TONE[a.severity] ?? 'info'}`}>
                  {a.severity}
                </span>
                <div className="alerts__body">
                  <p className="alerts__message">{a.message}</p>
                  {/* `alerts__meta` y no `alerts__action`: esta última ya
                      existe y está estilada como un botón para la pantalla
                      de pendientes. Reutilizar la clase equivocada pinta una
                      caja alrededor de una frase que no es pulsable. */}
                  <p className="alerts__meta">
                    {a.action}
                    {a.articleRef ? <span className="alerts__ref"> · {a.articleRef}</span> : null}
                  </p>
                </div>
                {a.dueDate ? <span className="alerts__date">{a.dueDate}</span> : null}
              </li>
            ))}
          </ul>
          {alerts.shown.length === 0 && alerts.grouped.length > 0 && (
            <p className="screen__note">
              Las alertas del mismo tipo se agrupan en una sola línea. Repetir seis mil avisos
              iguales no informa: enseña a ignorarlos.
            </p>
          )}
        </div>
      )}

      {/* ── Indicadores ──────────────────────────────────────────────── */}
      {loading && <p className="table__loading">Calculando indicadores…</p>}

      {data && (
        <div className="panel">
          <h2 className="panel__title">Indicadores del periodo</h2>
          <div className="cards">
            {data.kpis.map((kpi) => {
              const trend = trendOf(kpi.code);
              return (
                <div className={`card${kpi.value === null ? ' card--muted' : ''}`} key={kpi.code}>
                  <p className="card__label">{kpi.label}</p>
                  <p className="card__value">{formatValue(kpi)}</p>

                  {/* El denominador SIEMPRE: «94 % de 17» y «94 % de 1.240»
                      no se leen igual, y sin él nadie puede saber cuál es. */}
                  <p className="card__meta">
                    {miles(kpi.numerator)} de {miles(kpi.denominator)} · {kpi.denominatorMeaning}
                  </p>

                  {!kpi.reliable && (
                    <p className="card__warn">
                      ⚠ Muestra insuficiente: el dato se enseña pero no se puede concluir nada de él.
                    </p>
                  )}
                  {kpi.note && <p className="card__meta">{kpi.note}</p>}

                  {trend?.deltaPercent && (
                    <p
                      className={`card__trend${trend.improving === false ? ' card__trend--bad' : ''}${
                        trend.improving === true ? ' card__trend--good' : ''
                      }`}
                    >
                      {trend.improving === true ? '▲' : trend.improving === false ? '▼' : '■'}{' '}
                      {trend.deltaPercent} % respecto al periodo anterior
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <p className="screen__note">
            Un indicador sin muestra suficiente vale <strong>«sin datos»</strong>, no cero. Un cero
            dice «lo hicimos mal»; «sin datos» dice «no lo sabemos», y son cosas distintas sobre las
            que se deciden cosas distintas.
          </p>
        </div>
      )}
    </section>
  );
}
